import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  Paper,
  Typography,
} from "@mui/material";

import {
  Close,
  ContentCopy,
  ExpandMore,
} from "@mui/icons-material";

import { useSelectedOnlineNetworks } from "../../../state/application-state-service";

import {
  useNetwork,
  useStoredLocationData,
  useStoredLocationParams,
} from "../../../state/services";

import { useInputParamState } from "../../../state/Input-param-state";

import {
  copyParams,
} from "../../../state/services";

import {
  createTrackSectionFromGeoJson,
  type GeoJsonTrackPoint,
} from "../../../utils/onlineMapsUtils";


import { MultiSelectDropdown } from "../../../components/SidePanelTab";


type CopyParameter = {
  source_coord: string;
  source_name: string;
  id: string
};

type OnlineCopyDialogProps = {
  open: boolean;
  handleClose: () => void;
  copyParam: CopyParameter;
  accessToken: string;
};

type CoordinateOption = {
  id: string;
  name: string;
  coords: string;
  index: number;
  point: GeoJsonTrackPoint;
};

type SelectedValueMap = Record<string, unknown>;

type TargetLocation = {
  latitude: number;
  longitude: number;
  altitude: number;
  network_id: string;
  section_id: string;
};

export function OnlineCopyDialog({
  open,
  handleClose,
  copyParam,
  accessToken,
}: OnlineCopyDialogProps) {
  const geoJsonData = useSelectedOnlineNetworks();
  const selectedNetwork = useNetwork();

  const paramState = useInputParamState();
  const locations = useStoredLocationParams();
  const locationData = useStoredLocationData();

  const descriptionElementRef =
    useRef<HTMLElement>(null);

  const [expanded, setExpanded] =
    useState(true);

  const [
    selectedValues,
    setSelectedValues,
  ] = useState<CoordinateOption[]>([]);

  /**
   * Convert the online GeoJSON into one synthetic track section.
   */
  const section = useMemo(
    () =>
      createTrackSectionFromGeoJson(geoJsonData, {
        networkId:
          selectedNetwork.id ||
          "selected-online-network",
        trackId:
          selectedNetwork.id ||
          "selected-online-network",
        trackName:
          selectedNetwork.name ||
          "Selected Track",
      }),
    [
      geoJsonData,
      selectedNetwork.id,
      selectedNetwork.name,
    ]
  );

  /**
   * Convert the GeoJSON points into dropdown options.
   *
   * GeoJSON coordinates are:
   * [longitude, latitude]
   *
   * Existing application location keys are:
   * "latitude,longitude"
   */
  const coordinateOptions =
    useMemo<CoordinateOption[]>(() => {
      if (!section) {
        return [];
      }

      return section.points.map(
        (point, index) => {
          const [longitude, latitude] =
            point.coordinates;

          return {
            id: point.featureId,
            name:
              point.name ||
              `Node ${index + 1}`,
            coords: `${latitude},${longitude}`,
            index,
            point,
          };
        }
      );
    }, [section]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const descriptionElement =
      descriptionElementRef.current;

    descriptionElement?.focus();
  }, [open]);

  /**
   * Reset the internal state whenever the dialog closes.
   */
  useEffect(() => {
    if (open) {
      return;
    }

    setSelectedValues([]);
    setExpanded(true);
  }, [open]);

  const handleAccordionChange = (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded);
  };

  const handleSelectedValuesChange = (
    selected: any
  ) => {
    setSelectedValues(selected ?? []);
  };

  const resetAndClose = () => {
    setSelectedValues([]);
    setExpanded(true);
    handleClose();
  };

  const copyData = () => {
    if (!section) {
      return;
    }

    const sourceParameters =
      locations[copyParam.source_coord];

    const sourceResult =
      locationData[copyParam.source_coord];

    if (
      !sourceParameters ||
      !sourceResult ||
      selectedValues.length === 0
    ) {
      return;
    }

    const parameters: Record<
      string,
      SelectedValueMap
    > = {};

    const results: any = {};

    const coordDetails: Array<
      Record<string, unknown>
    > = [];

    const targetLocations: TargetLocation[] =
      [];

    const networkName = selectedNetwork.name

    selectedValues.forEach((item) => {
      const [latitudeText, longitudeText] =
        item.coords.split(",");

      const latitude =
        Number(latitudeText);

      const longitude =
        Number(longitudeText);

      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude)
      ) {
        return;
      }

      /**
       * Keep the old copied-location shape.
       *
       * The synthetic section uses the network ID
       * as its railwayEdgeId.
       */
      parameters[item.coords] = {
        ...sourceParameters,
        id: section.railwayEdgeId,
      };

      results[item.coords] = {
        ...sourceResult,
        id: section.railwayEdgeId,
      };

      /**
       * findCoordDetails can still be reused because
       * createTrackSectionFromGeoJson produces an
       * old-style section containing lineString.coordinates.
       */
        const coordinateDetail = {
            coord: [
                latitude,
                longitude,
            ],
            id: item.id,
            name: item.name
        }

      if (coordinateDetail) {
        coordDetails.push(coordinateDetail);
      }

      const sectionName =
        item.id ||
        `${section.railwayEdgeName}.${item.index}`;

      targetLocations.push({
        latitude,
        longitude,
        altitude: 30,
        network_id: networkName,
        section_id: sectionName,
      });
    });

    if (
      Object.keys(parameters).length === 0
    ) {
      return;
    }

    copyParams(
      paramState,
      parameters,
      results,
      coordDetails,
      copyParam.id,
      targetLocations,
      networkName,
      accessToken
    );

    resetAndClose();
  };

  const canCopy =
    Boolean(
      locations[copyParam.source_coord]
    ) &&
    Boolean(
      locationData[copyParam.source_coord]
    ) &&
    selectedValues.length > 0;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
      aria-labelledby="online-copy-dialog-title"
      aria-describedby="online-copy-dialog-description"
    >
      <DialogTitle id="online-copy-dialog-title">
        Select target coordinates
      </DialogTitle>

      <DialogContent dividers>
        <DialogContentText
          id="online-copy-dialog-description"
         
          tabIndex={-1}
          component="div"
        >
          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            Select the node coordinates where the
            parameters from{" "}
            <strong>
              {copyParam.source_name}
            </strong>{" "}
            should be copied.
          </Typography>

          {selectedValues.length > 0 && (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
              >
                Selected coordinates (
                {selectedValues.length})
              </Typography>

              <Grid
                container
                spacing={1}
              >
                {selectedValues.map(
                  (item) => (
                    <Grid
                      item
                      key={item.id}
                    >
                      <Chip
                        label={item.name}
                        onDelete={() => {
                          setSelectedValues(
                            (previous) =>
                              previous.filter(
                                (selected) =>
                                  selected.id !==
                                  item.id
                              )
                          );
                        }}
                        sx={{
                          color: "green",
                          borderColor: "black",
                          backgroundColor:
                            "white",
                        }}
                        variant="outlined"
                      />
                    </Grid>
                  )
                )}
              </Grid>
            </Paper>
          )}

          <List disablePadding>
            {section && (
              <Accordion
                disableGutters
                expanded={expanded}
                onChange={
                  handleAccordionChange
                }
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: expanded
                      ? "#33ae3319"
                      : "#fdfdfd",
                    borderBottom: expanded
                      ? "1px solid #33ae33"
                      : "1px solid #eeeeee",
                    "&:hover": {
                      bgcolor: "#33ae3319",
                      borderBottom:
                        "1px solid #33ae33",
                    },
                  }}
                >
                  <Grid
                    container
                    spacing={1}
                  >
                    <Grid item xs={12}>
                      <Typography variant="caption">
                        Track Section
                      </Typography>

                      <Typography
                        component="div"
                        sx={{
                          color: "green",
                        }}
                      >
                        {
                          section.railwayEdgeName
                        }{" "}
                        (
                        {Math.round(
                          section.length
                        )}
                        m)
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      m: 1,
                    }}
                  >
                    {coordinateOptions.length >
                    0 ? (
                      <MultiSelectDropdown
                        options={
                          coordinateOptions
                        }
                        selectedOptions={
                          selectedValues
                        }
                        onChange={
                          handleSelectedValuesChange
                        }
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        No node coordinates are
                        available for this network.
                      </Typography>
                    )}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
          </List>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          endIcon={<Close />}
          onClick={resetAndClose}
          sx={{
            textTransform: "none",
          }}
        >
          Cancel
        </Button>

        <Button
          variant="outlined"
          color="success"
          disabled={!canCopy}
          onClick={copyData}
          endIcon={<ContentCopy />}
          sx={{
            textTransform: "none",
          }}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}